import { Camera, RefreshCw } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'

interface CameraOCRProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onScanSuccess: (plate: string) => void
}

export function CameraOCR({ open, onOpenChange, onScanSuccess }: CameraOCRProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [isScanning, setIsScanning] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [stream, setStream] = useState<MediaStream | null>(null)

  const startCamera = useCallback(async () => {
    setError(null)
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      })
      setStream(mediaStream)
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream
      }
    } catch (err) {
      console.error('Erro ao acessar câmera:', err)
      setError('Não foi possível acessar a câmera do dispositivo.')
    }
  }, [])

  const stopCamera = useCallback(() => {
    if (videoRef.current && videoRef.current.srcObject) {
      const mediaStream = videoRef.current.srcObject as MediaStream
      mediaStream.getTracks().forEach((track) => track.stop())
      videoRef.current.srcObject = null
    }
    setStream(null)
  }, [])

  useEffect(() => {
    if (open) {
      startCamera()
    } else {
      stopCamera()
    }
    return () => stopCamera()
  }, [open, startCamera, stopCamera])

  const extractPlateFromText = (text: string) => {
    const cleanText = text.replace(/[^A-Za-z0-9]/g, '').toUpperCase()
    // Mercosul (ex: ABC1D23) ou Antigo (ex: ABC1234)
    const mercosulMatch = cleanText.match(/[A-Z]{3}[0-9][A-Z0-9][0-9]{2}/)
    const legacyMatch = cleanText.match(/[A-Z]{3}[0-9]{4}/)

    if (mercosulMatch) return mercosulMatch[0]
    if (legacyMatch) return legacyMatch[0]
    return null
  }

  const captureAndScan = async () => {
    if (!videoRef.current || !canvasRef.current) return

    setIsScanning(true)
    setError(null)

    const video = videoRef.current
    const canvas = canvasRef.current
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
    // Extrai imagem em base64 e remove o cabeçalho data:image/png;base64,
    const base64Image = canvas.toDataURL('image/jpeg', 0.8)
    const base64Data = base64Image.replace(/^data:image\/(png|jpeg|jpg);base64,/, '')

    try {
      const formData = new FormData()
      formData.append('base64Image', `data:image/jpeg;base64,${base64Data}`)
      formData.append('apikey', 'helloworld')
      formData.append('language', 'eng')
      formData.append('OCREngine', '2')

      const res = await fetch('https://api.ocr.space/parse/image', {
        method: 'POST',
        body: formData,
      })
      const data = await res.json()

      if (data.IsErroredOnProcessing || !data.ParsedResults || data.ParsedResults.length === 0) {
        throw new Error('Nenhuma placa detectada pela API OCR.')
      }

      const textResult = data.ParsedResults[0].ParsedText
      const foundPlate = extractPlateFromText(textResult)

      if (foundPlate) {
        // Formata placa para o formato correto (AAA-1234 ou AAA1A23)
        let formatted = foundPlate
        if (formatted.length === 7 && !formatted.includes('-')) {
          formatted = `${formatted.substring(0, 3)}-${formatted.substring(3)}`
        }
        onScanSuccess(formatted)
        onOpenChange(false)
      } else {
        setError('Não foi possível identificar uma placa válida na imagem. Tente novamente.')
      }
    } catch (err: any) {
      console.error('Falha no OCR:', err)
      setError(err.message || 'Erro de comunicação com o serviço OCR.')
    } finally {
      setIsScanning(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px] bg-slate-900 border-slate-700 text-slate-100">
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center gap-2">
            <Camera className="text-blue-500" />
            Scanner de Placa (OCR)
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col items-center justify-center gap-4 mt-2">
          {error && (
            <div className="bg-red-500/20 text-red-400 p-3 rounded-md text-sm text-center w-full">
              {error}
            </div>
          )}

          <div className="relative w-full aspect-video bg-black rounded-lg overflow-hidden border border-slate-700 flex items-center justify-center">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
            />

            {/* Guide overlay */}
            <div className="absolute inset-0 border-[3px] border-dashed border-blue-500/50 m-8 rounded"></div>

            {isScanning && (
              <div className="absolute inset-0 bg-slate-900/60 flex items-center justify-center backdrop-blur-sm">
                <div className="flex flex-col items-center gap-2">
                  <RefreshCw className="animate-spin text-blue-400" size={32} />
                  <span className="font-medium text-slate-200">Lendo placa...</span>
                </div>
              </div>
            )}
          </div>

          {/* Canvas oculto para tirar o print */}
          <canvas ref={canvasRef} className="hidden" />

          <div className="flex justify-between gap-4 w-full mt-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="w-full bg-transparent border-slate-700 hover:bg-slate-800 text-slate-300"
            >
              Cancelar
            </Button>
            <Button
              onClick={captureAndScan}
              disabled={isScanning || !stream}
              className="w-full bg-blue-600 hover:bg-blue-700 font-bold gap-2"
            >
              <Camera size={18} />
              {isScanning ? 'Lendo...' : 'Capturar Placa'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
